import * as React from 'react';
import classNames from 'classnames';
import RCNotification from 'rc-notification';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import ExclamationCircleFilled from '@ant-design/icons/ExclamationCircleFilled';
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled';
import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled';
import InfoCircleFilled from '@ant-design/icons/InfoCircleFilled';
import createUseMessage from './hooks/useMessage';
import { globalConfig } from '../config-provider';
let messageInstance;
let defaultDuration = 3;
let defaultTop;
let key = 1;
let localPrefixCls = '';
let transitionName = 'move-up';
let hasTransitionName = false;
let getContainer;
let maxCount;
let rtl = false;
export function getKeyThenIncreaseKey() {
    return key++;
}
function setMessageConfig(options) {
    if (options.top !== undefined) {
        defaultTop = options.top;
        messageInstance = null; // delete messageInstance for new defaultTop
    }
    if (options.duration !== undefined) {
        defaultDuration = options.duration;
    }
    if (options.prefixCls !== undefined) {
        localPrefixCls = options.prefixCls;
    }
    if (options.getContainer !== undefined) {
        getContainer = options.getContainer;
    }
    if (options.transitionName !== undefined) {
        transitionName = options.transitionName;
        messageInstance = null; // delete messageInstance for new transitionName
        hasTransitionName = true;
    }
    if (options.maxCount !== undefined) {
        maxCount = options.maxCount;
        messageInstance = null;
    }
    if (options.rtl !== undefined) {
        rtl = options.rtl;
    }
}
function getRCNotificationInstance(args, callback) {
    const { prefixCls: customizePrefixCls } = args;
    const { getPrefixCls, getRootPrefixCls } = globalConfig();
    const prefixCls = getPrefixCls('message', customizePrefixCls || localPrefixCls);
    const rootPrefixCls = getRootPrefixCls(args.rootPrefixCls, prefixCls);
    if (messageInstance) {
        callback({ prefixCls, rootPrefixCls, instance: messageInstance });
        return;
    }
    const instanceConfig = {
        prefixCls,
        transitionName: hasTransitionName ? transitionName : `${rootPrefixCls}-${transitionName}`,
        style: { top: defaultTop },
        getContainer,
        maxCount,
    };
    RCNotification.newInstance(instanceConfig, (instance) => {
        if (messageInstance) {
            callback({ prefixCls, rootPrefixCls, instance: messageInstance });
            return;
        }
        messageInstance = instance;
        if (process.env.NODE_ENV === 'test') {
            messageInstance.config = instanceConfig;
        }
        callback({ prefixCls, rootPrefixCls, instance });
    });
}
const typeToIcon = {
    info: InfoCircleFilled,
    success: CheckCircleFilled,
    error: CloseCircleFilled,
    warning: ExclamationCircleFilled,
    loading: LoadingOutlined,
};
function getRCNoticeProps(args, prefixCls) {
    const duration = args.duration !== undefined ? args.duration : defaultDuration;
    const IconComponent = typeToIcon[args.type];
    const messageClass = classNames(`${prefixCls}-custom-content`, {
        [`${prefixCls}-${args.type}`]: args.type,
        [`${prefixCls}-rtl`]: rtl === true,
    });
    return {
        key: args.key,
        duration,
        style: args.style || {},
        className: args.className,
        content: (<div className={messageClass}>
        {args.icon || (IconComponent && <IconComponent />)}
        <span>{args.content}</span>
      </div>),
        onClose: args.onClose,
        onClick: args.onClick,
    };
}
function notice(args) {
    const target = args.key || key++;
    const closePromise = new Promise(resolve => {
        const callback = () => {
            if (typeof args.onClose === 'function') {
                args.onClose();
            }
            return resolve(true);
        };
        getRCNotificationInstance(args, ({ prefixCls, instance }) => {
            instance.notice(getRCNoticeProps(Object.assign(Object.assign({}, args), { key: target, onClose: callback }), prefixCls));
        });
    });
    const result = () => {
        if (messageInstance) {
            messageInstance.removeNotice(target);
        }
    };
    result.then = (filled, rejected) => closePromise.then(filled, rejected);
    result.promise = closePromise;
    return result;
}
function isArgsProps(content) {
    return (Object.prototype.toString.call(content) === '[object Object]' &&
        !!content.content);
}
const api = {
    open: notice,
    config: setMessageConfig,
    destroy(messageKey) {
        if (messageInstance) {
            if (messageKey) {
                const { removeNotice } = messageInstance;
                removeNotice(messageKey);
            }
            else {
                const { destroy } = messageInstance;
                destroy();
                messageInstance = null;
            }
        }
    },
};
export function attachTypeApi(originalApi, type) {
    originalApi[type] = (content, duration, onClose) => {
        if (isArgsProps(content)) {
            return originalApi.open(Object.assign(Object.assign({}, content), { type }));
        }
        if (typeof duration === 'function') {
            onClose = duration;
            duration = undefined;
        }
        return originalApi.open({ content, duration, type, onClose });
    };
}
['success', 'info', 'warning', 'error', 'loading'].forEach(type => attachTypeApi(api, type));
api.warn = api.warning;
api.useMessage = createUseMessage(getRCNotificationInstance, getRCNoticeProps);
/** @private test Only function. Not work on production */
export const getInstance = () => (process.env.NODE_ENV === 'test' ? messageInstance : null);
export default api;
